/**
 * @Module: Shopping List Context
 * @Status: Ready
 * @Summary: Persistent shopping list with Firestore + localStorage dual storage
 * @Description: Tracks items the user needs to buy. Items can be added from
 *   PantryCheck, recipe shopping lists, or manually. Checked items can be
 *   synced back to pantry ("bought these? add to pantry").
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ShoppingItem } from '../types/shopping-list';
import { useAuth } from './AuthContext';
import { isFirebaseConfigured, db } from '../config/firebase';
import { doc, getDoc, setDoc, type Firestore } from 'firebase/firestore';
import { util_log_debug_01 } from '../utils/logging';

const STORAGE_KEY = 'choppy:shoppingList';

interface ShoppingListContextValue {
  items: ShoppingItem[];
  loading: boolean;
  addItems: (items: Omit<ShoppingItem, 'addedAt' | 'isChecked'>[]) => void;
  removeItem: (name: string) => void;
  toggleItem: (name: string) => void;
  clearChecked: () => void;
  clearAll: () => void;
  getCheckedItems: () => ShoppingItem[];
}

const ShoppingListContext = createContext<ShoppingListContextValue | undefined>(undefined);

function loadLocal(): ShoppingItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveLocal(items: ShoppingItem[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
  catch { /* quota */ }
}

function shoppingDoc(database: Firestore, uid: string) {
  return doc(database, 'users', uid, 'profile', 'shoppingList');
}

export const ShoppingListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const firestoreDown = useRef(false);
  const uid = user?.uid ?? null;

  // Load on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);

      if (uid && isFirebaseConfigured() && db && !firestoreDown.current) {
        try {
          const snap = await getDoc(shoppingDoc(db, uid));
          if (snap.exists()) {
            const data = snap.data();
            const loaded = (data.items as ShoppingItem[]) || [];
            setItems(loaded);
            saveLocal(loaded);
            util_log_debug_01('ShoppingListContext: loaded from Firestore');
            setLoading(false);
            return;
          }
        } catch {
          firestoreDown.current = true;
        }
      }

      setItems(loadLocal());
      setLoading(false);
    };
    load();
  }, [uid]);

  const persist = useCallback((updated: ShoppingItem[]) => {
    setItems(updated);
    saveLocal(updated);

    if (uid && isFirebaseConfigured() && db && !firestoreDown.current) {
      setDoc(shoppingDoc(db, uid), { items: updated }, { merge: true }).catch(() => {
        firestoreDown.current = true;
      });
    }
  }, [uid]);

  const addItems = useCallback((newItems: Omit<ShoppingItem, 'addedAt' | 'isChecked'>[]) => {
    setItems(prev => {
      const existing = new Set(prev.map(i => i.name.toLowerCase()));
      const toAdd = newItems
        .filter(i => !existing.has(i.name.toLowerCase()))
        .map(i => ({
          ...i,
          addedAt: new Date().toISOString(),
          isChecked: false,
        }));
      const updated = [...prev, ...toAdd];
      persist(updated);
      return updated;
    });
  }, [persist]);

  const removeItem = useCallback((name: string) => {
    setItems(prev => {
      const updated = prev.filter(i => i.name.toLowerCase() !== name.toLowerCase());
      persist(updated);
      return updated;
    });
  }, [persist]);

  const toggleItem = useCallback((name: string) => {
    setItems(prev => {
      const updated = prev.map(i =>
        i.name.toLowerCase() === name.toLowerCase()
          ? { ...i, isChecked: !i.isChecked }
          : i
      );
      persist(updated);
      return updated;
    });
  }, [persist]);

  const clearChecked = useCallback(() => {
    setItems(prev => {
      const updated = prev.filter(i => !i.isChecked);
      persist(updated);
      return updated;
    });
  }, [persist]);

  const clearAll = useCallback(() => {
    persist([]);
  }, [persist]);

  const getCheckedItems = useCallback((): ShoppingItem[] => {
    return items.filter(i => i.isChecked);
  }, [items]);

  return (
    <ShoppingListContext.Provider value={{
      items, loading, addItems, removeItem, toggleItem,
      clearChecked, clearAll, getCheckedItems,
    }}>
      {children}
    </ShoppingListContext.Provider>
  );
};

export const useShoppingList = (): ShoppingListContextValue => {
  const ctx = useContext(ShoppingListContext);
  if (!ctx) throw new Error('useShoppingList must be used inside <ShoppingListProvider>');
  return ctx;
};
